import React, { useMemo, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
// Aliased on purpose: imported as `Error` it SHADOWS the global constructor,
// and every `new Error(...)` in this file would build the React component
// instead — an object with no `message`, so the reason would be lost.
import ErrorAlert from '../../../Components/Error';
import OperatorService from '../../../Services/OperatorService';
import {
  newSiblingRecord, operatorId, pairedRecord, settlementCurrencyPlan, siblingName,
} from '../operatorGroups';

/**
 * "This company also settles in HUF."
 *
 * That sentence is the whole of what the user is expressing, and the whole
 * of what this modal says back. Underneath, a second Operator record is what
 * makes it true — one record, one `settlement_currency`, so every figure has
 * an unambiguous bucket — and the two are tied together so one portal login
 * answers for both. None of that is the user's problem, so none of it is on
 * the screen: no ids, no "records", no `absorbed_ids`.
 *
 * Two ways it can go, and the difference matters:
 *
 *  - The other bucket already exists, unpaired — which is how ops has been
 *    running these companies by hand. It is ADOPTED. Minting a third record
 *    beside two live ones would split the company's money three ways.
 *  - It does not exist. It is CREATED from this record's own contact and
 *    billing details, then paired.
 *
 * If the create lands but the pairing fails, the new bucket is left behind
 * and named in the error. Asking again finds it as a candidate and pairs it,
 * so the retry completes the job instead of duplicating it.
 */
const CREATE = '__create__';

const failureMessage = (err) => err?.response?.data?.message
  || err?.message
  || 'Something went wrong. Nothing was changed.';

function AddSettlementCurrencyModal({
  operator = {}, operators = [], show = false, handleClose, fetchOperators,
}) {
  const plan = useMemo(
    () => settlementCurrencyPlan(operator, operators),
    [operator, operators],
  );

  const [choice, setChoice] = useState(
    plan.candidates.length ? operatorId(plan.candidates[0]) : CREATE,
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const { blocked, target, group } = plan;
  const adopting = plan.candidates.find((row) => operatorId(row) === choice);

  /** The operators as they stand on the server, not as the screen has them. */
  const currentRows = async () => {
    const fetched = await OperatorService.fetchOperators();

    return fetched?.data?.data || [];
  };

  /**
   * Find the record the create just made when the response did not carry it.
   * Matched on the name this modal chose plus the currency it asked for —
   * both of which only the record it just created can have.
   */
  const findCreated = async (name) => {
    const rows = await currentRows();

    return rows.find((row) => row.name === name && row.settlement_currency === target);
  };

  /**
   * Read the pairing back before calling it done.
   *
   * The API leaves `absorbed_ids` alone whenever a request omits it — and an
   * API that does not yet know the field drops it just as quietly, answering
   * 200 either way. Reporting that as success would leave the company with
   * two buckets, unlinked, and nobody looking for them.
   */
  const confirmPaired = async (siblingId) => {
    const saved = (await currentRows())
      .find((row) => operatorId(row) === operatorId(operator));

    if (saved && !(saved.absorbed_ids || []).includes(siblingId)) {
      throw new Error('The pairing was not saved.');
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    let siblingId = adopting ? operatorId(adopting) : '';
    const name = siblingName(operator.name, target);

    try {
      if (!siblingId) {
        const created = await OperatorService.saveOperator(newSiblingRecord(operator, target));
        siblingId = operatorId(created?.data?.data || created?.data);

        if (!siblingId) siblingId = operatorId(await findCreated(name));
      }

      if (!siblingId) {
        throw new Error(`${target} was set up but could not be linked. Try again.`);
      }
    } catch (err) {
      setError(failureMessage(err));
      setSaving(false);
      return;
    }

    try {
      // Only now, and only here: the surviving record is the one that keeps
      // its history and its login, so it is the one that answers for both.
      await OperatorService.saveOperator(pairedRecord(operator, siblingId));
      await confirmPaired(siblingId);
      await fetchOperators();
      handleClose();
    } catch (err) {
      setError(`${failureMessage(err)} ${adopting ? '' : `${name} was set up but is not linked yet — ask again to finish.`}`.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="sm">
      <Modal.Header closeButton>
        <Modal.Title>Add settlement currency</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorAlert error={error} setError={setError} />

        {blocked ? (
          <p className="mb-0" role="alert">{blocked}</p>
        ) : (
          <Form>
            <p>
              {`${group.company} settles in ${operator.settlement_currency}. `}
              {`Add ${target} so it can be paid in both.`}
            </p>

            {plan.candidates.length ? (
              <Form.Group className="mb-3 col-12" controlId="sibling">
                <Form.Label>{`${target} settlement`}</Form.Label>
                <Form.Select value={choice} onChange={(e) => setChoice(e.target.value)}>
                  {plan.candidates.map((candidate) => (
                    <option key={operatorId(candidate)} value={operatorId(candidate)}>
                      {`Use the existing ${target} setup (${candidate.name})`}
                    </option>
                  ))}
                  <option value={CREATE}>{`Set up a new ${target} settlement`}</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {`${group.company} already has a ${target} setup on file. Using it keeps every past `
                    + `${target} trip, payment and invoice where it is.`}
                </Form.Text>
              </Form.Group>
            ) : (
              <Form.Text className="text-muted d-block mb-3">
                {`Contact and billing details are copied from the ${operator.settlement_currency} `
                  + `settlement. ${operator.settlement_currency} figures and ${target} figures stay `
                  + 'separate — they are never added together.'}
              </Form.Text>
            )}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Cancel</Button>
        {!blocked && (
          <Button onClick={handleSubmit} disabled={saving}>
            {`Add ${target}`}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default AddSettlementCurrencyModal;
